/**
 * HEIC/HEIF (the iPhone photo format) can't be shown or drawn by most
 * browsers (Chrome, Edge, Firefox, Android), so every photo that comes in
 * goes through here first: a HEIC is converted to a JPEG, anything else is
 * returned untouched. Safari decodes HEIC itself, so it skips the conversion.
 *
 * The converter (libheif compiled to WebAssembly, a few MB) is loaded only
 * when a HEIC actually needs converting, never with the normal page.
 */

// Major brands that mark a HEIC/HEIF file in its "ftyp" box.
const HEIF_BRANDS = ['heic', 'heix', 'hevc', 'hevx', 'heim', 'heis', 'mif1', 'msf1'];

/** HEIC by content (some systems report an empty type) or, failing that, by type/extension. */
export async function isHeicFile(file: File): Promise<boolean> {
  try {
    const head = new Uint8Array(await file.slice(0, 12).arrayBuffer());
    if (head.length >= 12 && String.fromCharCode(...head.slice(4, 8)) === 'ftyp') {
      return HEIF_BRANDS.includes(String.fromCharCode(...head.slice(8, 12)));
    }
  } catch { /* unreadable: fall through to the name check */ }
  return /^image\/hei[cf]/i.test(file.type) || /\.hei[cf]$/i.test(file.name);
}

/**
 * Returns a JPEG when `file` is a HEIC this browser can't decode; otherwise the
 * same file. Never throws: if the conversion fails the original comes back and
 * the caller's normal "unsupported image" handling takes over.
 */
export async function normalizeImageFile(file: File): Promise<File> {
  if (!(await isHeicFile(file))) return file;

  // Safari (and some others) can already decode it.
  const native = await createImageBitmap?.(file).catch(() => null);
  if (native) {
    native.close();
    return file;
  }

  try {
    const { heicTo } = await import('heic-to/csp');
    const jpeg = await heicTo({ blob: file, type: 'image/jpeg', quality: 0.9 });
    return new File([jpeg], file.name.replace(/\.hei[cf]$/i, '') + '.jpg', { type: 'image/jpeg' });
  } catch {
    return file;
  }
}
